import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { List, X, SignOut, CaretDown, UserCircle, GlobeSimple } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const { user, logout } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();
  
  // Dynamic data from API
  const [siteSettings, setSiteSettings] = useState(null);
  const [navigation, setNavigation] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchSiteSettings();
    fetchNavigation();
    fetchServices();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const res = await fetch(`${API}/site-settings`);
      if (res.ok) setSiteSettings(await res.json());
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const fetchNavigation = async () => {
    try {
      const res = await fetch(`${API}/navigation`);
      if (res.ok) setNavigation(await res.json());
    } catch (error) {
      console.error('Error fetching navigation:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API}/services-list`);
      if (res.ok) setServices(await res.json());
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Get logo URL
  const logoUrl = siteSettings?.logo_url || 'https://customer-assets.emergentagent.com/job_aspbd-central/artifacts/fg24f6g6_SP%20BD%20GROUP%20logo.png';
  const displayLogoUrl = logoUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${logoUrl}` : logoUrl;

  // Filter visible navigation items
  const visibleNav = navigation.filter(n => n.visible);
  const visibleServices = services.filter(s => s.visible);

  return (
    <nav className="sticky top-0 z-50 glass-effect bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src={displayLogoUrl} 
              alt={siteSettings?.site_name || 'SP BD Group'} 
              className="h-14 w-auto"
              data-testid="navbar-logo"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-10">
            {visibleNav.map((item) => {
              if (item.is_dropdown) {
                return (
                  <div 
                    key={item.nav_id}
                    className="relative group"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <button 
                      className="text-base font-bold text-gray-700 hover:text-[#6BBE45] transition inline-flex items-center space-x-1"
                      data-testid={`nav-${item.nav_id}-dropdown`}
                    >
                      <span>{item.label}</span>
                      <CaretDown size={16} weight="bold" className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {servicesOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-white border border-gray-200 rounded-sm shadow-2xl py-3 z-[100]">
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-200 rotate-45"></div>
                        {visibleServices.map((service) => (
                          <Link
                            key={service.service_id}
                            to={service.path}
                            className="block px-5 py-3 text-base font-medium text-gray-700 hover:bg-gradient-to-r hover:from-[#6BBE45]/10 hover:to-transparent hover:text-[#6BBE45] transition-all duration-200"
                            data-testid={`nav-${service.service_id}-link`}
                          >
                            {service.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link 
                  key={item.nav_id}
                  to={item.path} 
                  className="text-base font-bold text-gray-700 hover:text-[#6BBE45] transition" 
                  data-testid={`nav-${item.nav_id}-link`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          <div className="hidden md:flex items-center space-x-4">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1.5 px-3 py-2 text-sm font-bold text-gray-600 hover:text-[#6BBE45] transition border border-gray-200 rounded-sm hover:border-[#6BBE45]/50"
              data-testid="language-switcher"
              aria-label={language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
            >
              <GlobeSimple size={18} weight="bold" />
              <span>{language === 'es' ? 'ES' : 'EN'}</span>
            </button>
            
            {user ? (
              <div className="flex items-center space-x-3">
                {user.role === 'admin' && (
                  <Link to="/admin" className="text-base font-bold text-[#DC2626] hover:text-[#B91C1C] transition" data-testid="nav-admin-link">{t('nav.adminPanel')}</Link>
                )}
                <Link 
                  to="/profile" 
                  className="flex items-center space-x-2 text-base font-medium text-gray-600 hover:text-[#6BBE45] transition"
                  data-testid="nav-profile-link"
                >
                  <UserCircle size={24} />
                  <span data-testid="user-name">{user.name}</span>
                </Link>
                <button 
                  onClick={handleLogout} 
                  className="p-2 text-gray-600 hover:text-[#FF3B3B] transition"
                  data-testid="logout-button"
                  title={t('nav.logout')}
                >
                  <SignOut size={22} />
                </button>
              </div>
            ) : (
              <Link 
                to="/login" 
                className="px-7 py-3 bg-[#6BBE45] text-white text-base font-bold rounded-sm hover:bg-[#5AA838] transition"
                data-testid="nav-login-button"
              >
                {t('nav.login')}
              </Link>
            )}
          </div>

          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={28} /> : <List size={28} />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 pt-2 pb-4 space-y-3">
            {/* Mobile Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 w-full py-3 text-base font-medium text-gray-700 border-b border-gray-100"
              data-testid="mobile-language-switcher"
            >
              <GlobeSimple size={20} weight="bold" className="text-[#6BBE45]" />
              <span>{language === 'es' ? 'Español (ES)' : 'English (EN)'}</span>
              <span className="ml-auto text-sm text-gray-500">{language === 'es' ? 'Switch to EN' : 'Cambiar a ES'}</span>
            </button>
            
            {visibleNav.map((item) => {
              if (item.is_dropdown) {
                return (
                  <div key={item.nav_id}>
                    <button 
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className="flex items-center justify-between w-full py-3 text-base font-medium text-gray-700"
                      data-testid={`mobile-nav-${item.nav_id}-toggle`}
                    >
                      <span>{item.label}</span>
                      <CaretDown size={18} weight="bold" className={`transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileServicesOpen && (
                      <div className="pl-4 space-y-2 mt-2">
                        {visibleServices.map((service) => (
                          <Link
                            key={service.service_id}
                            to={service.path}
                            className="block py-2 text-base text-gray-600"
                            data-testid={`mobile-nav-${service.service_id}-link`}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {service.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link 
                  key={item.nav_id}
                  to={item.path} 
                  className="block py-3 text-base font-medium text-gray-700" 
                  data-testid={`mobile-nav-${item.nav_id}-link`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              );
            })}
            {user ? (
              <>
                {user.role === 'admin' && <Link to="/admin" className="block py-3 text-base font-medium text-[#DC2626]" data-testid="mobile-nav-admin-link" onClick={() => setMobileMenuOpen(false)}>{t('nav.adminPanel')}</Link>}
                <Link to="/profile" className="block py-3 text-base font-medium text-gray-700" data-testid="mobile-nav-profile-link" onClick={() => setMobileMenuOpen(false)}>{t('nav.profile')}</Link>
                <button onClick={handleLogout} className="block w-full text-left py-3 text-base font-medium text-[#FF3B3B]" data-testid="mobile-logout-button">{t('nav.logout')}</button>
              </>
            ) : (
              <Link to="/login" className="block py-3 text-base font-bold text-[#6BBE45]" data-testid="mobile-nav-login-link" onClick={() => setMobileMenuOpen(false)}>{t('nav.login')}</Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
